// @ts-nocheck
/*
 * Agents list — v1 logic preserved end-to-end. 10s polling, dual render
 * modes (regular vs consolidatedCallCentre for call-centre-admin overview),
 * CLI allocation with dedicated-CLI radio, bulk CSV upload + result modal,
 * bulk logout, per-row login-as flow (Soft Phone / Mobile / WEBRTC),
 * country codes / activity / call-quality modals, agent-details modal.
 *
 * Chrome swap: react-bootstrap Row/Col/Card/Modal/Form/Button/Dropdown →
 * shadcn Card/Dialog/ConfirmDialog/DropdownMenu + native form-control
 * inputs and search bar. react-select and existing partial modals stay.
 */
import { useState, useEffect, useRef } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { canBulkUploadAgents } from '@/utilities/promptUploadAccess';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Loader2, Search } from 'lucide-react';
import {
  Flag,
  Phone,
  Mail,
  User,
  Calendar,
  CalendarClock,
  Shield,
  Info,
  Eye,
  PhoneForwarded,
  Activity as ActivityIcon,
  Signal,
  MoreVertical,
  Ban,
  Unlock,
  Smartphone,
  Radio,
  Headphones as HeadphonesIcon,
  LogIn,
  KeyRound,
} from 'lucide-react';
import { FiInfo, FiEdit } from 'react-icons/fi';
import type { ColumnDef } from '@tanstack/react-table';
import ReactSelect from 'react-select';
import { DataTable } from '@/components/data-table/DataTable';
import downloadTemplate from '@/utilities/downloadCsv';
import Loader from '@/components/partials/components/Loader';
import {
  API_AGENTS_LIST,
  API_AGENT_CREATE_LICENCE,
  API_AGENT_BULK_LOGOUT,
  API_AGENT_BULK_UPLOAD,
  API_AGENT_AVAILABLE_INBOUND_NUMBERS_BY_ID,
  API_AGENT_CLI_ALLOCATIONS_SYNC,
  API_USER_COUNTRY_CODE_DIRECTIONS_BY_AGENT_IDS,
  API_SWITCH_USER,
  API_USERS_LIST,
  API_USER_STATUS_UPDATE,
} from '@/service/url';
import CountryCodeDirectionsModal from '@/components/partials/dashboard/country-code-direction/CountryCodeDirectionsModal';
import AgentActivityModal from '@/features/agent-activity/AgentActivityModal';
import SipTraceModal from '@/features/sip-trace/SipTraceModal';
import AgentCallQualityModal from './AgentCallQualityModal';
import BreakStatusCapsule from '@/components/BreakStatusCapsule';
import WrapUpStatus from '@/components/WrapUpStatus';
import { getApiCall, postApiCall, postApiMediaCall, patchApiCallWithHeader } from '@/service/httpCall';
import {
  UPLOAD_BULK_AGENT_CREATE,
  UPLOAD_BULK_AGENT_CREATE_REQUIREMENTS,
  UPLOAD_BULK_AGENT_CREATE_SAMPLE_DATE,
} from '@/utilities/constant';
import InfoNote from '@/components/InfoNote';
import { getUser, getUserReadAccessRoleLabel, switchUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES, ROUTES } from '@/utilities/constant';
import RenderInactiveUserModal from '@/components/partials/components/inactive-user-modal';
import RenderShowPasswordModal from '@/components/partials/components/show-password';
import RenderUserHierarchyModal from '@/components/partials/components/user-hierarchy-modal';
import { exportToCSV, hasPermission } from '@/utilities/helpers';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { isExpiredDate } from '@/features/users/components/UserStatusBadge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { bulkSetUserStatus } from '@/utilities/bulkUserStatus';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Download, Plus, Network } from 'lucide-react';
import CopyButton from '@/features/api-webhook/components/CopyButton';

const copyBtnClass =
  'ml-1 inline-flex h-5 w-5 shrink-0 p-0 align-middle border-0 bg-transparent shadow-none text-muted-foreground hover:text-foreground [&_svg]:h-3 [&_svg]:w-3';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

// Most outbound CLIs one agent can be given in the Allocate CLI modal.
const MAX_AGENT_CLIS = 5;

const normalizeBulkAgentResult = (response) => {
  const responseData = response?.data?.data || response?.data || response;
  const successRows = Array.isArray(responseData?.success) ? responseData.success : [];
  const failedRows = Array.isArray(responseData?.failed) ? responseData.failed : [];
  return {
    totalProcessed: successRows.length + failedRows.length,
    successCount: successRows.length,
    failedCount: failedRows.length,
    successRows,
    failedRows,
  };
};

const getCsvField = (row, name) => {
  if (!row || typeof row !== 'object') return undefined;
  if (row[name] !== undefined) return row[name];
  if (row[`${name} - Required`] !== undefined) return row[`${name} - Required`];
  if (row[`${name} - Optional`] !== undefined) return row[`${name} - Optional`];
  return undefined;
};

const formatSuccessAgent = (item, index) => {
  const a = item?.agent || {};
  const name = a.agent_name || getCsvField(item?.row, 'Agent Name') || '-';
  const number = a.agent_number || getCsvField(item?.row, 'Agent Number') || '-';
  const username = a.username || getCsvField(item?.row, 'Username') || '-';
  return `Row ${index + 1} | ${name} | Number: ${number} | Username: ${username}`;
};

const formatFailedAgent = (item, index) => {
  const row = item?.row || {};
  const name = getCsvField(row, 'Agent Name') || '-';
  const number = getCsvField(row, 'Agent Number') || '-';
  const username = getCsvField(row, 'Username') || '-';
  const error = item?.error || 'Unknown error';
  return `Row ${index + 1} | ${name} | Number: ${number} | Username: ${username} | Reason: ${error}`;
};

const formatAgentAclDisplay = (details) => {
  if (!details) return 'Not Assigned';
  const acl = details.linkedUser?.acl_settings;
  const aclId = acl?.id ?? details.linkedUser?.acl_settings_id;
  if (acl?.acl_name && aclId) return `${acl.acl_name} (ID: ${aclId})`;
  if (acl?.acl_name) return acl.acl_name;
  if (aclId) return `ID: ${aclId}`;
  return 'Not Assigned';
};

const isAllocateCliSelectNode = (target: EventTarget | null | undefined) => {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest('[class*="allocate-cli-select"]'));
};

const EXTENSION_PROFILE_TYPES = ['WEBRTC', 'SOFT PHONE'];

const getAgentExtensions = (rowData: any) => {
  const profiles = rowData?.agentTelephony?.profile;
  if (!Array.isArray(profiles)) return '-';
  const extensions = profiles
    .filter((p: any) =>
      EXTENSION_PROFILE_TYPES.includes(String(p?.type || '').toUpperCase()),
    )
    .map((p: any) => p?.number?.number)
    .filter(Boolean)
    .map(String);
  return extensions.length ? [...new Set(extensions)].join(', ') : '-';
};

const AgentsListCenterCell = ({ children }: any) => (
  <div className="agents-list-cell-center">{children}</div>
);

const AgentListInlineButton = ({ children, onClick, disabled, title }: any) => (
  <Button
    type="button"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="gap-1.5 px-2.5 py-1 text-xs"
  >
    {children}
  </Button>
);

const Agents = ({ consolidatedCallCentre = false }: any) => {
  const navigate = useNavigate();
  const [agentList, setAgentList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState<any[]>([]);
  const [statusList, setStatusList] = useState<any>({});
  const [buttonDisable, setButtonDisable] = useState(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [bulkFile, setBulkFile] = useState<any>(null);
  const [showBulkResultModal, setShowBulkResultModal] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [newStatus, setNewStatus] = useState(false);
  const [agentId, setAgentId] = useState<any>('');
  const [activeType, setActiveType] = useState('');
  const [types, setTypes] = useState<any[]>([]);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [loggedInUserId, setLoggedInUserId] = useState<any>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loggingInType, setLoggingInType] = useState<any>(null);
  const [pendingTypeLogin, setPendingTypeLogin] = useState<{
    agentId: any;
    agentName: string;
    type: string;
    isActive: boolean;
  } | null>(null);
  const [showBulkLogoutModal, setShowBulkLogoutModal] = useState(false);
  const [bulkStatusTarget, setBulkStatusTarget] = useState<0 | 1 | null>(null); // 0 = block, 1 = unblock
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);
  const agentUserRef = useRef(new Map<any, { userId: any; name: string }>());
  const [bulkLogoutLoading, setBulkLogoutLoading] = useState(false);
  const [bulkAddSaving, setBulkAddSaving] = useState(false);
  const bulkLogoutInFlightRef = useRef(false);
  const [showCcDirectionsModal, setShowCcDirectionsModal] = useState(false);
  const [ccDirectionsData, setCcDirectionsData] = useState<any[]>([]);
  const [ccDirectionsOwner, setCcDirectionsOwner] = useState('');
  const [, setIsFirstLoad] = useState(true);
  const [showAllocateCliModal, setShowAllocateCliModal] = useState(false);
  const [allocatingAgent, setAllocatingAgent] = useState<any>(null);
  const [availableCliDids, setAvailableCliDids] = useState<any[]>([]);
  const [selectedCliDidIds, setSelectedCliDidIds] = useState<number[]>([]);
  const [dedicatedCliNumberId, setDedicatedCliNumberId] = useState<any>(null);
  const [loadingCliDids, setLoadingCliDids] = useState(false);
  const [savingCliDid, setSavingCliDid] = useState(false);
  const [globalFilter, setGlobalFilter] = usePersistedState('manage-agents/AgentsList:globalFilter', '');
  const [activityAgent, setActivityAgent] = useState<any>(null);
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);
  const [hierarchyUser, setHierarchyUser] = useState<any>(null);
  const [sipTraceAgent, setSipTraceAgent] = useState<any>(null);
  const [callQualityAgent, setCallQualityAgent] = useState<any>(null);
  const [showViewClisModal, setShowViewClisModal] = useState(false);
  const [viewClisAgent, setViewClisAgent] = useState<any>(null);
  const [viewClisList, setViewClisList] = useState<any[]>([]);
  const [viewClisLoading, setViewClisLoading] = useState(false);
  const [selectedAgentDetails, setSelectedAgentDetails] = useState<any>(null);
  const [showAgentDetailsModal, setShowAgentDetailsModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => Math.floor(Date.now() / 1000));
  const [enableInactiveModal, setEnableInactiveModal] = useState(false);
  const [inactiveUserId, setInactiveUserId] = useState<any>(undefined);
  const [confirmUserInactive, setConfirmUserInactive] = useState(false);
  const [enableShowPasswordModal, setEnableShowPasswordModal] = useState(false);
  const [showPasswordId, setShowPasswordId] = useState<any>(undefined);
  const [, setConfirmShowPassword] = useState(false);
  const [showPasswordUser, setShowPasswordUser] = useState<any>(null);
  const [activeUserAccessRole, setActiveUserAccessRole] = useState<any>(undefined);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [checkingCreateLicence, setCheckingCreateLicence] = useState(false);

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState<any>({});
  const [, setShowToast] = useState(false);
  const [, setToastProps] = useState<any>(null);

  const agentsListPath = `/${ROUTES.MANAGE_AGENTS.AGENTS}`;
  const setAgentsReturnPath = () => {
    if (consolidatedCallCentre) sessionStorage.setItem('agentsReturnTo', agentsListPath);
    else sessionStorage.removeItem('agentsReturnTo');
  };

  const openCreateAgent = async () => {
    if (checkingCreateLicence) return;
    setCheckingCreateLicence(true);
    try {
      const licence: any = await getApiCall(API_AGENT_CREATE_LICENCE);
      if (!licence?.allowed) {
        toast.error(licence?.message || 'You do not have a licence to create an agent.');
        return;
      }
      setAgentsReturnPath();
      navigate('/create-agent');
    } catch {
      // getApiCall already shows the request error.
    } finally {
      setCheckingCreateLicence(false);
    }
  };

  const getColumnsAndFilename = () => {
    const columns = UPLOAD_BULK_AGENT_CREATE;
    const requirements = UPLOAD_BULK_AGENT_CREATE_REQUIREMENTS;
    const sample_data = UPLOAD_BULK_AGENT_CREATE_SAMPLE_DATE;
    const filename = 'Bulk_Create_Agents.csv';
    return { columns, requirements, filename, sample_data };
  };

  // Server-side paging + search (opt-in ?page on API_AGENTS_LIST; other callers still get the full array).
  const [paging, setPaging] = usePersistedState('manage-agents/AgentsList:paging', { pageIndex: 0, pageSize: 10 });
  const [totalAgents, setTotalAgents] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState(() => globalFilter.trim());
  const [pageLoading, setPageLoading] = useState(false);
  const queryRef = useRef({ paging, search: debouncedSearch });
  queryRef.current = { paging, search: debouncedSearch };
  const reqSeqRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = globalFilter.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) setPaging((p) => ({ ...p, pageIndex: 0 }));
        return next;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [globalFilter]);

  const mapAgentRow = (val, prevDirectionsById = new Map()) => ({
    id: val.id,
    agent_name: val.agent_name,
    agent_number: val.agent_number,
    is_allocated: val.is_allocated,
    created_by: val.created_by?.username || 'Unknown',
    created_by_id: val.created_by?.id ?? null,
    created_at: new Date(val.created_at).toLocaleString(),
    created_at_raw: val.created_at,
    extension: val.extension?.map((ext) => ext.extension).join(', ') || 'N/A',
    username: val.username,
    type: val.type,
    access: val.access,
    email_id: val.email_id,
    status: val.login_status === '1' ? 'Active' : 'Inactive',
    is_submitting_disposition: val.is_submitting_disposition === true,
    call_status: val.call_status,
    is_logged_in: val.linkedUser?.login_at != null && val.linkedUser?.login_at !== undefined,
    account_status: val.linkedUser?.status,
    is_blocked: val.linkedUser != null && Number(val.linkedUser.status) === 0,
    expiry_date: val.linkedUser?.expiry_date || null,
    cli_option: val.cli_option ? val.cli_option.toUpperCase() : '-',
    is_outbound_enabled: val.is_outbound_enabled === true,
    auto_pick_enabled: val.auto_pick_enabled === true,
    show_auto_answer_toggle: val.show_auto_answer_toggle === true,
    is_transfer_enabled: val.is_transfer_enabled === true,
    is_conference_enabled: val.is_conference_enabled === true,
    allow_early_follow_up: val.allow_early_follow_up === true,
    is_masking_enabled: val.is_masking_enabled === true,
    agentTelephony: val.agentTelephony,
    country_code_directions: prevDirectionsById.get(val.id) || [],
    first_call_acceptance: val.first_call_acceptance?.toUpperCase(),
    break_id: val.break_id,
    breakDetail: val.breakDetail,
    agent_updated_at: val.updated_at,
    linkedUser: val.linkedUser || null,
    activeUserBreak: val.activeUserBreak || null,
  });

  const fetchAgents = (showLoader = false) => {
    if (showLoader) setLoading(true);
    const { paging: pg, search } = queryRef.current;
    const seq = ++reqSeqRef.current;
    const params = new URLSearchParams({ page: String(pg.pageIndex + 1), limit: String(pg.pageSize) });
    if (search) params.set('search', search);
    getApiCall(`${API_AGENTS_LIST}?${params.toString()}`)
      .then((page: any) => {
        if (seq !== reqSeqRef.current) return; // stale (page/search changed meanwhile)
        // Old backend (no paging support) still answers with the full array.
        const resp = Array.isArray(page) ? page : page?.rows;
        if (Array.isArray(resp)) {
          const total = Array.isArray(page) ? page.length : Number(page.total) || 0;
          setTotalAgents(total);
          // Last row on a page deleted/filtered away -> step back a page.
          if (resp.length === 0 && pg.pageIndex > 0 && total > 0) {
            setPaging((p) => ({ ...p, pageIndex: Math.max(0, Math.ceil(total / p.pageSize) - 1) }));
          }
          resp.forEach((val) =>
            agentUserRef.current.set(val.id, {
              userId: val.linkedUser?.id,
              name: val.agent_name || val.linkedUser?.username,
            }),
          );
          setAgentList((prev) => {
            const prevDirectionsById = new Map(
              prev.map((agent) => [agent.id, agent.country_code_directions]),
            );
            return resp.map((val) => mapAgentRow(val, prevDirectionsById));
          });

          const ids = resp.map((val) => val.id);
          if (ids.length > 0) {
            getApiCall(`${API_USER_COUNTRY_CODE_DIRECTIONS_BY_AGENT_IDS}?ids=${ids.join(',')}`)
              .then((map: any) => {
                if (!map || typeof map !== 'object') return;
                setAgentList((prev) =>
                  prev.map((a) => ({
                    ...a,
                    country_code_directions:
                      map[a.id] !== undefined ? map[a.id] || [] : a.country_code_directions,
                  })),
                );
              })
              .catch(() => {});
          }

          setStatusList(
            resp.reduce((acc, val) => {
              acc[val.id] = val.login_status === '1';
              return acc;
            }, {}),
          );
        } else {
          toast.error('Failed to fetch agents');
        }
      })
      .catch((err) => {
        if (seq !== reqSeqRef.current) return;
        console.error('Fetch Error:', err);
        toast.error('Error fetching agents');
      })
      .finally(() => {
        if (showLoader) setLoading(false);
        if (seq === reqSeqRef.current) setPageLoading(false);
      });
  };

  // Page / page-size / search change -> fetch that page (initial load handled below).
  const isFirstQueryRef = useRef(true);
  useEffect(() => {
    if (isFirstQueryRef.current) {
      isFirstQueryRef.current = false;
      return;
    }
    setPageLoading(true);
    fetchAgents(false);
  }, [paging.pageIndex, paging.pageSize, debouncedSearch]);

  useEffect(() => {
    const user = getUser();
    if (consolidatedCallCentre && user?.role !== USER_ROLES.CALL_CENTRE_ADMIN) {
      navigate(`/${ROUTES.MANAGE_AGENTS.AGENTS}`, { replace: true });
      return;
    }
    setLoggedInUserId(user?.id ?? null);
    if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    const role = getUserReadAccessRoleLabel()?.[0]?.value;
    if (role) setActiveUserAccessRole(role);
    fetchAgents(true);
    setIsFirstLoad(false);
    const interval = setInterval(() => fetchAgents(false), 10000);
    return () => clearInterval(interval);
  }, [consolidatedCallCentre]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!confirmUserInactive || !inactiveUserId) return;
    patchApiCallWithHeader(`${API_USER_STATUS_UPDATE}/${inactiveUserId}`, { status: 0 })
      .then(() => {
        toast.success('Agent blocked successfully');
        setEnableInactiveModal(false);
        setInactiveUserId(undefined);
        setConfirmUserInactive(false);
        fetchAgents();
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Unable to block agent');
        setConfirmUserInactive(false);
      });
  }, [confirmUserInactive, inactiveUserId]);

  useEffect(() => {
    if (!enableShowPasswordModal || !showPasswordId || !activeUserAccessRole) return;
    getApiCall(`${API_USERS_LIST}/${showPasswordId}`, { role: activeUserAccessRole })
      .then((resp: any) => {
        if (resp?.actual_password) setShowPasswordUser(`Password is : ${resp.actual_password}`);
        else setShowPasswordUser('Actual Password is not saved for this user');
        setShowPasswordId(undefined);
        setConfirmShowPassword(false);
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Unable to fetch password');
        setEnableShowPasswordModal(false);
      });
  }, [enableShowPasswordModal, showPasswordId, activeUserAccessRole]);

  const openAgentDetailsModal = (rowData) => {
    setSelectedAgentDetails(rowData);
    setShowAgentDetailsModal(true);
  };

  const handleBulkAdd = () => setShowBulkAddModal(true);
  const handleBulkAddFileChange = (event) => setBulkFile(event.target.files[0]);

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings)
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleBulkAddSave = async () => {
    if (bulkAddSaving) return;
    if (!bulkFile) return void toast.error('Please select a file to upload.');
    const fileName = bulkFile.name.toLowerCase();
    if (!fileName.endsWith('.csv')) return void toast.error('Invalid file type. Please upload a CSV file.');
    const formData = new FormData();
    formData.append('file', bulkFile);
    setBulkAddSaving(true);
    try {
      const response = await postApiMediaCall(API_AGENT_BULK_UPLOAD, formData);
      const normalized = normalizeBulkAgentResult(response);
      setBulkUploadResult(normalized);
      setShowBulkAddModal(false);
      setBulkFile(null);
      setShowBulkResultModal(true);

      if (normalized.successCount > 0 && normalized.failedCount === 0) {
        toast.success(`${normalized.successCount} agent(s) uploaded successfully.`);
      } else if (normalized.successCount > 0) {
        toast.warning(`Uploaded ${normalized.successCount} agent(s); ${normalized.failedCount} row(s) failed.`);
      } else {
        toast.error(`No agents were created. ${normalized.failedCount} row(s) failed validation.`);
      }
      fetchAgents();
    } catch (err: any) {
      const errorMessage = friendlyApiError(err) || 'Failed to upload bulk agents';
      toast.error(
        `${errorMessage}\n\nPlease ensure:\n- File is in CSV format\n- All required fields are present\n- Data format is correct\n- Download and use the template for reference`,
      );
      setShowBulkAddModal(false);
      setBulkFile(null);
    } finally {
      setBulkAddSaving(false);
    }
  };

  const handleStatusToggle = async (agentIdArg, agentTelephony, typeArg) => {
    const nextStatus = !statusList[agentIdArg];
    setNewStatus(nextStatus);
    setAgentId(agentIdArg);
    const nextTypes = agentTelephony?.map((item) => item.type);
    setTypes(nextTypes);
    let nextActiveType = typeArg;
    if (!nextActiveType && agentTelephony) {
      const activeProfile = agentTelephony.find((p) => p.active_profile === true);
      nextActiveType = activeProfile?.type;
    }
    try {
      if (nextStatus) {
        setShowModal(true);
      } else {
        if (!nextActiveType) {
          toast.error(`Cannot logout: No active login type found for this agent`);
          return;
        }
        const params = { type: nextActiveType, login_status: nextStatus ? '1' : '0' };
        toast.success(`Logged Out From: ${nextActiveType}`);
        setActiveType('');
        fetchAgents();
      }
    } catch {
      toast.error(`Error while Logged Out From: ${nextActiveType || 'Unknown'}`);
    }
  };

  async function loginAs(typeArg) {
    try {
      setIsLoggingIn(true);
      setLoggingInType(typeArg);
      const params = { type: typeArg, login_status: newStatus ? '1' : '0' };
      toast.success(`Logged In as: ${typeArg}`);
      setActiveType('');
      setShowModal(false);
      fetchAgents();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || `Error while Logged In as: ${typeArg}`);
    } finally {
      setIsLoggingIn(false);
      setLoggingInType(null);
    }
  }

  const confirmTypeLogin = async () => {
    if (!pendingTypeLogin) return;
    const { agentId: aid, type: typeArg } = pendingTypeLogin;
    try {
      setIsLoggingIn(true);
      setLoggingInType(typeArg);
      const params = { type: typeArg, login_status: '1' };
      // toggleStatus API disabled — unguarded licence writer, races with logoutUser/logoutAs/reaper causing licence count drift.
      // await postApiCall(`/v2/agents/status/${aid}`, params);
      toast.success(`Logged In as: ${typeArg}`);
      fetchAgents();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || `Error while Logged In as: ${typeArg}`);
    } finally {
      setIsLoggingIn(false);
      setLoggingInType(null);
      setPendingTypeLogin(null);
    }
  };

  const loggedInCompanyDemo = (() => {
    try {
      const u = getUser();
      const c = u?.assignedCompany || u?.assigned_company_details || u?.company || null;
      return c?.is_demo_company === true;
    } catch {
      return false;
    }
  })();

  const handleAgentLogout = () => {
    if (selectedAgents.length === 0 || bulkLogoutInFlightRef.current) return;
    bulkLogoutInFlightRef.current = true;
    setBulkLogoutLoading(true);
    postApiCall(API_AGENT_BULK_LOGOUT, { agentIds: selectedAgents })
      .then(() => {
        toast.success('Agents Logged Out successfully');
        fetchAgents();
        setSelectedAgents([]);
        setShowBulkLogoutModal(false);
      })
      .catch((err) => {
        toast.error(friendlyApiError(err));
        fetchAgents();
        setSelectedAgents([]);
      })
      .finally(() => {
        bulkLogoutInFlightRef.current = false;
        setBulkLogoutLoading(false);
      });
  };

  const handleBulkStatus = async () => {
    if (bulkStatusTarget == null || bulkStatusLoading || selectedAgents.length === 0) return;
    const seen = selectedAgents.map((id) => agentUserRef.current.get(id));
    const userIds = seen.filter((a) => a?.userId).map((a) => Number(a.userId));
    const noLogin = seen.length - userIds.length;
    if (noLogin) toast.warning(`Skipped ${noLogin} agent(s) with no login account`);
    if (!userIds.length) return void setBulkStatusTarget(null);
    setBulkStatusLoading(true);
    const nameByUser = new Map(seen.filter((a) => a?.userId).map((a) => [Number(a.userId), a.name]));
    const ok = await bulkSetUserStatus(userIds, bulkStatusTarget, 'agent', (id) => nameByUser.get(id));
    setBulkStatusLoading(false);
    setBulkStatusTarget(null);
    if (ok) {
      fetchAgents();
      setSelectedAgents([]);
    }
  };

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      // Table holds one page only; export still covers every agent (legacy full-list response).
      const all: any = await getApiCall(API_AGENTS_LIST).catch(() => null);
      if (!Array.isArray(all)) {
        toast.error('Unable to export agents');
        return;
      }
      exportToCSV({
        fileName: 'agents-list',
        data: all.map((val) => mapAgentRow(val)),
        columns: [
          { header: 'Agent Name', accessor: 'agent_name' },
          { header: 'Agent Number', accessor: 'agent_number' },
          { header: 'Access', accessor: (row) => row.access || '-' },
          { header: 'Email ID', accessor: (row) => row.email_id || '-' },
          { header: 'Extensions', accessor: (row) => getAgentExtensions(row) },
          // { header: 'Cli Option (Webrtc)', accessor: (row) => row.cli_option || '-' }, // cli_option removed: always actual
          { header: 'Outbound Enabled', accessor: (row) => (row.is_outbound_enabled ? 'Yes' : 'No') },
          { header: 'Auto Answer', accessor: (row) => (row.auto_pick_enabled ? 'Yes' : 'No') },
          { header: 'Auto Answer Shown to Agent', accessor: (row) => (row.show_auto_answer_toggle ? 'Yes' : 'No') },
          { header: 'Transfer', accessor: (row) => (row.is_transfer_enabled ? 'Yes' : 'No') },
          { header: 'Conference', accessor: (row) => (row.is_conference_enabled ? 'Yes' : 'No') },
          { header: 'Early Follow-up Call', accessor: (row) => (row.allow_early_follow_up ? 'Yes' : 'No') },
          { header: 'Number Masking', accessor: (row) => (row.is_masking_enabled ? 'Yes' : 'No') },
          { header: 'Created By', accessor: (row) => row.created_by || '-' },
          { header: 'Joining Date', accessor: (row) => row.created_at || '-' },
          {
            header: 'Allocation Status',
            accessor: (row) => (Number(row.is_allocated) === 0 ? 'Available' : 'Allocated'),
          },
          { header: 'Account Status', accessor: (row) => (row.is_blocked ? 'Blocked' : 'Active') },
          {
            header: 'Login Status',
            accessor: (row) =>
              row.linkedUser?.login_at && !row.linkedUser?.logout_at ? 'Logged In' : 'Logged Out',
          },
          {
            header: 'Login Types',
            accessor: (row) => row.agentTelephony?.profile?.map((item) => item.type)?.join(', ') || '-',
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const onCheckboxChange = (checked, agentIdArg) =>
    setSelectedAgents((prev) => (checked ? [...prev, agentIdArg] : prev.filter((id) => id !== agentIdArg)));
  const onHeaderCheckboxChange = (checked) => {
    const pageIds = agentList.map((a) => a.id);
    setSelectedAgents((prev) =>
      checked
        ? Array.from(new Set([...prev, ...pageIds]))
        : prev.filter((id) => !pageIds.includes(id)),
    );
  };

  const allocationStatusBodyTemplate = (rowData) =>
    Number(rowData.is_allocated) === 0 ? 'Available' : 'Allocated';

  const handleEditAgent = (agentIdArg) => {
    setAgentsReturnPath();
    const editPath = ROUTES.MANAGE_AGENTS.EDIT_AGENT.replace(':id', agentIdArg);
    navigate(`/${editPath}`);
  };

  const handleBlockAgent = (linkedUserId) => {
    setInactiveUserId(linkedUserId);
    setEnableInactiveModal(true);
  };

  const handleUnblockAgent = (linkedUserId) => {
    patchApiCallWithHeader(`${API_USER_STATUS_UPDATE}/${linkedUserId}`, { status: 1 })
      .then(() => {
        toast.success('Agent unblocked successfully');
        fetchAgents();
      })
      .catch((err) => toast.error(friendlyApiError(err) || 'Unable to unblock agent'));
  };

  const handleShowAgentPassword = (linkedUserId) => {
    setShowPasswordId(linkedUserId);
    setEnableShowPasswordModal(true);
  };

  const handleResetAgentPassword = (linkedUserId) => {
    setAgentsReturnPath();
    const resetRoute = ROUTES.USERS.RESET_PASSWORD.replace(':id', linkedUserId);
    navigate(`/${resetRoute}`);
  };

  async function switchToAgentAccount(linkedUserId, accountStatus) {
    if (isSwitchingAccount) return;
    if (!linkedUserId) {
      toast.error('Agent user account not found');
      return;
    }
    if (Number(accountStatus) === 0 || accountStatus === 'Blocked') {
      toast.error('Agent is blocked');
      return;
    }
    setIsSwitchingAccount(true);
    try {
      const response: any = await postApiCall(API_SWITCH_USER, { id: linkedUserId });
      const switchAccountSuccess = await switchUser(response);
      if (switchAccountSuccess) {
        toast.success('Account switched successfully');
        setTimeout(() => {
          window.location.href = `/${ROUTES.DASHBOARD}`;
        }, 1500);
      } else {
        setIsSwitchingAccount(false);
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to switch account');
      setIsSwitchingAccount(false);
    }
  }

  const formatDateTime = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isDirectParentOfAgent = (rowData) => {
    if (isSuperAdmin) return true;
    if (loggedInUserId == null || rowData?.created_by_id == null) return false;
    return Number(rowData.created_by_id) === Number(loggedInUserId);
  };

  const hierarchyTargetForAgent = (rowData) => {
    if (rowData?.linkedUser?.id)
      return { id: rowData.linkedUser.id, username: rowData.username || rowData.agent_name };
    if (rowData?.created_by_id != null)
      return { id: rowData.created_by_id, username: rowData.created_by };
    return null;
  };

  const openHierarchyModal = (rowData) => {
    const target = hierarchyTargetForAgent(rowData);
    if (!target) return;
    setHierarchyUser(target);
    setShowHierarchyModal(true);
  };

  const renderSwitchToAgentCell = (rowData) => {
    const linkedUserId = rowData.linkedUser?.id;
    const accountStatus = rowData.account_status;
    const canSwitch = isDirectParentOfAgent(rowData);
    const active =
      Number(accountStatus) === 1 || accountStatus === 'Active' || accountStatus === 1;

    if (!linkedUserId || !canSwitch) return null;

    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        title={
          !active
            ? 'Cannot switch to a blocked agent'
            : isSwitchingAccount
              ? 'Switching account…'
              : `Switch to ${rowData.username || rowData.agent_name}`
        }
        disabled={!active || isSwitchingAccount}
        onClick={() => switchToAgentAccount(linkedUserId, accountStatus)}
      >
        <LogIn className="h-4 w-4" />
      </Button>
    );
  };

  const renderConsolidatedActions = (rowData) => {
    const linkedUserId = rowData.linkedUser?.id;
    const canEditUsers = loggedInUserACL?.[MODULE_ACL.USERS]?.edit;
    const isOwnAgent = isDirectParentOfAgent(rowData);
    const canEditAgents = isOwnAgent && (isSuperAdmin || loggedInUserACL?.[MODULE_ACL.AGENTS]?.edit);
    const accountStatus = rowData.account_status;
    const isBlocked = rowData.is_blocked;

    return (
      <div className="agents-list-actions-cell">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15"
              title="Row actions"
              aria-label="Row actions"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="max-h-[320px] overflow-y-auto">
            {linkedUserId && isOwnAgent && (
              <DropdownMenuItem
                disabled={isBlocked || isSwitchingAccount}
                onClick={() => switchToAgentAccount(linkedUserId, accountStatus)}
              >
                Switch To Agent
              </DropdownMenuItem>
            )}
            {canEditAgents && (
              <DropdownMenuItem onClick={() => handleEditAgent(rowData.id)}>Edit Agent</DropdownMenuItem>
            )}
            {canEditAgents && (
              <DropdownMenuItem
                disabled={rowData.is_logged_in}
                onClick={() => openAllocateCliModal(rowData)}
              >
                Allocate CLI
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setActivityAgent(rowData)}>View Activity</DropdownMenuItem>
            {isSuperAdmin && (
              <DropdownMenuItem onClick={() => setSipTraceAgent(rowData)}>WebRTC Trace</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setCallQualityAgent(rowData)}>
              Call Quality Stats
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openViewClisModal(rowData)}>View CLIs</DropdownMenuItem>
            {hierarchyTargetForAgent(rowData) && (
              <DropdownMenuItem onClick={() => openHierarchyModal(rowData)}>
                View Hierarchy
              </DropdownMenuItem>
            )}
            {linkedUserId && canEditUsers && !loggedInCompanyDemo ? (
              isBlocked ? (
                <DropdownMenuItem onClick={() => handleUnblockAgent(linkedUserId)}>
                  Unblock Agent
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => handleBlockAgent(linkedUserId)}>
                  Block Agent
                </DropdownMenuItem>
              )
            ) : null}
            {linkedUserId && canShowPassword && (
              <DropdownMenuItem onClick={() => handleShowAgentPassword(linkedUserId)}>
                Show Password
              </DropdownMenuItem>
            )}
            {linkedUserId && canEditUsers && (
              <DropdownMenuItem onClick={() => handleResetAgentPassword(linkedUserId)}>
                Reset Password
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const openAllocateCliModal = async (rowData) => {
    if (rowData.is_logged_in) {
      toast.error(
        'Cannot allocate CLI: Agent is currently logged in. Please ask the agent to log out first.',
      );
      return;
    }
    setAllocatingAgent(rowData);
    setSelectedCliDidIds([]);
    setDedicatedCliNumberId(null);
    setShowAllocateCliModal(true);
    setLoadingCliDids(true);
    try {
      const list: any = await getApiCall(
        API_AGENT_AVAILABLE_INBOUND_NUMBERS_BY_ID.replace(':id', rowData.id),
      );
      setAvailableCliDids(Array.isArray(list) ? list : []);
      const current = (Array.isArray(list) ? list : [])
        .filter((n) => n.cli_target_type === 'AGENT' && String(n.cli_target_id) === String(rowData.id))
        .map((n) => Number(n.id))
        .filter((id) => Number.isFinite(id));
      setSelectedCliDidIds(current);
      const dedicatedMatch = (Array.isArray(list) ? list : []).find(
        (n) =>
          current.includes(Number(n.id)) &&
          n.inbound_routing_type === 'AGENT' &&
          String(n.inbound_routing_id) === String(rowData.id),
      );
      setDedicatedCliNumberId(
        dedicatedMatch && Number.isFinite(Number(dedicatedMatch.id)) ? Number(dedicatedMatch.id) : null,
      );
    } catch {
      toast.error('Failed to fetch available CLIs for allocation');
      setAvailableCliDids([]);
    } finally {
      setLoadingCliDids(false);
    }
  };

  const handleCliDidSelect = (values) => {
    const ids = (values || []).map((v) => Number(v)).filter((v) => Number.isFinite(v));
    // Removing is always fine (an agent may already have more than the limit); adding stops at the limit.
    if (ids.length > MAX_AGENT_CLIS && ids.length > selectedCliDidIds.length) {
      toast.error(`You can select up to ${MAX_AGENT_CLIS} CLIs for an agent.`);
      return;
    }
    setSelectedCliDidIds((prev) => {
      const kept = prev.filter((id) => ids.includes(id));
      const appended = ids.filter((id) => !kept.includes(id));
      return [...kept, ...appended];
    });
    setDedicatedCliNumberId((prev) => (prev != null && ids.includes(prev) ? prev : null));
  };

  const handleSaveAllocatedCliDid = async () => {
    if (!allocatingAgent?.id || savingCliDid) return;
    if (selectedCliDidIds.length > MAX_AGENT_CLIS) {
      toast.error(`You can select up to ${MAX_AGENT_CLIS} CLIs for an agent. Remove ${selectedCliDidIds.length - MAX_AGENT_CLIS} to save.`);
      return;
    }
    setSavingCliDid(true);
    try {
      await postApiCall(API_AGENT_CLI_ALLOCATIONS_SYNC.replace(':id', allocatingAgent.id), {
        numberIds: selectedCliDidIds,
        defaultNumberId: selectedCliDidIds?.[0] || null,
        dedicatedNumberIds: dedicatedCliNumberId != null ? [dedicatedCliNumberId] : [],
      });
      toast.success('CLI allocated successfully');
      setShowAllocateCliModal(false);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to allocate CLI');
    } finally {
      setSavingCliDid(false);
    }
  };

  const openViewClisModal = async (agent) => {
    if (!agent?.id) return;
    setViewClisAgent(agent);
    setViewClisList([]);
    setShowViewClisModal(true);
    setViewClisLoading(true);
    try {
      const list: any = await getApiCall(
        API_AGENT_AVAILABLE_INBOUND_NUMBERS_BY_ID.replace(':id', agent.id),
      );
      const allocated = (Array.isArray(list) ? list : []).filter(
        (n) => n.cli_target_type === 'AGENT' && String(n.cli_target_id) === String(agent.id),
      );
      setViewClisList(allocated);
    } catch {
      toast.error('Failed to fetch allocated CLIs');
      setViewClisList([]);
    } finally {
      setViewClisLoading(false);
    }
  };

  const isCallCentreAdmin = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.AGENTS,
    'read',
  );
  const canAdd =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.AGENTS, 'add');
  const canEdit =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.AGENTS, 'edit');
  const canBlock = canEdit;
  const canBulkBlock =
    !loggedInCompanyDemo &&
    (consolidatedCallCentre ? Boolean(loggedInUserACL?.[MODULE_ACL.USERS]?.edit) : canBlock);
  const canShowPassword =
    (isSuperAdmin || isCallCentreAdmin) && Boolean(loggedInUserACL?.[MODULE_ACL.USERS]?.read);
  const showActionsColumn = isCallCentreAdmin || isSuperAdmin;

  if (!canRead) {
    return (
      <div className="my-12 p-4 text-center text-base">
        No read permission found in assigned ACL
      </div>
    );
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      header: () => (
        <Checkbox
          checked={agentList.length > 0 && agentList.every((a) => selectedAgents.includes(a.id))}
          onCheckedChange={onHeaderCheckboxChange}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedAgents.includes(row.original.id)}
          onCheckedChange={(v) => onCheckboxChange(v, row.original.id)}
        />
      ),
    },
    {
      id: 'agent',
      header: 'Agent',
      cell: ({ row }) => {
        const r = row.original;
        const username = r.username || r.linkedUser?.username;
        return (
          <div className="min-w-[180px] text-left leading-tight">
            <div className="font-semibold">
              {r.agent_name || '-'}
              <CopyButton text={r.agent_name} className={copyBtnClass} />
              {r.expiry_date &&
                (isExpiredDate(r.expiry_date) ? (
                  <Badge variant="warning" className="ml-1 px-1.5 py-0 text-[10px]">Expired</Badge>
                ) : (
                  <Badge variant="success" className="ml-1 px-1.5 py-0 text-[10px]">Valid</Badge>
                ))}
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="opacity-70">Agent Number:</span> {r.agent_number || '-'}
              <CopyButton text={r.agent_number ? String(r.agent_number) : ''} className={copyBtnClass} />
            </div>
            {username && (
              <div className="text-xs text-muted-foreground">
                <span className="opacity-70">Username:</span> {username}
                <CopyButton text={username} className={copyBtnClass} />
              </div>
            )}
            {!consolidatedCallCentre && r.email_id && (
              <div className="flex items-center text-xs text-muted-foreground">
                <span className="truncate" title={r.email_id}>{r.email_id}</span>
                <CopyButton text={r.email_id} className={copyBtnClass} />
              </div>
            )}
            {!consolidatedCallCentre && r.expiry_date && (
              <div className="text-[11px] text-muted-foreground">Expires {formatDateTime(r.expiry_date)}</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'live_status',
      header: 'Live Status',
      cell: ({ row }) => {
        const rowData = row.original;
        const profiles = rowData?.agentTelephony?.profile || [];
        const localActiveType = profiles.find((p: any) => p.active_profile === true)?.type;
        const localTypes: string[] = profiles.map((item: any) => item.type);
        const typeIconFor = (t: string) => {
          const up = String(t || '').toUpperCase();
          if (up.includes('MOBILE')) return <Smartphone className="h-3.5 w-3.5" />;
          if (up.includes('WEBRTC')) return <Radio className="h-3.5 w-3.5" />;
          if (up.includes('SOFT')) return <HeadphonesIcon className="h-3.5 w-3.5" />;
          return <Phone className="h-3.5 w-3.5" />;
        };
        return (
          <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1 text-left">
            <span className="text-[11px] text-muted-foreground">Device</span>
            {localTypes.length === 0 ? (
              <span className="text-xs text-muted-foreground">—</span>
            ) : (
              <div className="flex items-center gap-1">
                {localTypes.map((t) => {
                  const isActive = localActiveType === t;
                  return (
                    <span
                      key={t}
                      aria-label={isActive ? `Currently active: ${t}` : `${t} configured`}
                      title={isActive ? `Currently active: ${t}` : `${t} configured`}
                      className={
                        'inline-flex h-6 w-6 items-center justify-center rounded-md ring-1 ring-inset ' +
                        (isActive
                          ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                          : 'bg-muted/60 text-muted-foreground ring-border/60')
                      }
                    >
                      {typeIconFor(t)}
                    </span>
                  );
                })}
              </div>
            )}
            <span className="text-[11px] text-muted-foreground">Dashboard</span>
            <div>
              {rowData.linkedUser?.login_at && !rowData.linkedUser?.logout_at ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800">
                  <span className="h-[7px] w-[7px] rounded-full bg-emerald-500" /> Logged In
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
                  <span className="h-[7px] w-[7px] rounded-full bg-slate-400" /> Logged Out
                </span>
              )}
            </div>
            <span className="text-[11px] text-muted-foreground">Call</span>
            <div>
              {rowData.call_status == 1 ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-blue-100 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300">
                  <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-blue-500" /> In Call
                </span>
              ) : rowData.status === 'Active' ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
                  <span className="h-[7px] w-[7px] rounded-full bg-slate-400" /> Idle
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </div>
            <span className="text-[11px] text-muted-foreground">Break</span>
            <div>
            <BreakStatusCapsule
              agent={{
                break_id: rowData.break_id,
                breakDetail: rowData.breakDetail,
                updated_at: rowData.agent_updated_at,
              }}
              user={rowData.linkedUser}
              activeUserBreak={rowData.activeUserBreak}
              currentTime={currentTime}
            />
            </div>
            <span className="text-[11px] text-muted-foreground">Wrap Up</span>
            <div>
              <WrapUpStatus submitting={rowData.is_submitting_disposition} loggedIn={rowData.status === 'Active'} />
            </div>
          </div>
        );
      },
    },
    {
      id: 'calling',
      header: 'Calling',
      cell: ({ row }) => {
        const r = row.original;
        const access =
          r.access === 'team_lead' ? 'TEAM LEAD' : r.access === 'regular' ? 'REGULAR' : r.access ? String(r.access).toUpperCase() : '-';
        const flag = (label: string, on: boolean) => (
          <span
            title={`${label}: ${on ? 'Enabled' : 'Disabled'}`}
            className={
              'whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ' +
              (on
                ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-muted/60 text-muted-foreground ring-border/60')
            }
          >
            {label}: {on ? 'On' : 'Off'}
          </span>
        );
        return (
          <div className="flex max-w-[200px] flex-wrap items-center justify-center gap-1">
            <span className="whitespace-nowrap rounded-full bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
              Access: {access}
            </span>
            {!consolidatedCallCentre && (
              <>
                {r.first_call_acceptance && (
                  <span className="whitespace-nowrap rounded-full bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground ring-1 ring-inset ring-border/60">
                    First Call: {r.first_call_acceptance}
                  </span>
                )}
                {flag('Outbound', Boolean(r.is_outbound_enabled))}
                {flag('Masking', Boolean(r.is_masking_enabled))}
              </>
            )}
          </div>
        );
      },
    },
    {
      id: 'numbers',
      header: 'Numbers',
      cell: ({ row }) => {
        const r = row.original;
        const ccRows = r.country_code_directions || [];
        const isLoggedIn = r.is_logged_in;
        const allocated = Number(r.is_allocated) !== 0;
        const iconBtn =
          'inline-flex h-7 items-center gap-1 rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 px-2 text-[11px] font-medium text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40';
        return (
          <div className="flex flex-col items-center gap-1">
            <div className="text-xs">
              <span className="text-muted-foreground">Ext:</span> {getAgentExtensions(r)}
            </div>
            {!consolidatedCallCentre && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <span className={'h-[7px] w-[7px] rounded-full ' + (allocated ? 'bg-emerald-500' : 'bg-slate-400')} />
                {allocationStatusBodyTemplate(r)}
              </span>
            )}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => openViewClisModal(r)}
                className={iconBtn}
                title="View allocated CLIs"
                aria-label="View allocated CLIs"
              >
                <Eye className="h-3.5 w-3.5" /> CLIs
              </button>
              {ccRows.length > 0 && (
                <button
                  type="button"
                  disabled={isLoggedIn}
                  onClick={() => {
                    if (isLoggedIn) {
                      toast.error(
                        'Cannot change country codes: Agent is currently logged in. Please ask the agent to log out first.',
                      );
                      return;
                    }
                    setCcDirectionsData(ccRows);
                    setCcDirectionsOwner(r.agent_name);
                    setShowCcDirectionsModal(true);
                  }}
                  className={iconBtn}
                  title={isLoggedIn ? 'Cannot change country codes while agent is logged in' : 'View country codes'}
                  aria-label="View country codes"
                >
                  <Flag className="h-3.5 w-3.5" /> {ccRows.length}
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'details',
      header: 'Details',
      cell: ({ row }) => (
        <AgentsListCenterCell>
          <button
            type="button"
            onClick={() => openAgentDetailsModal(row.original)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15"
            title="View details"
            aria-label="View details"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </AgentsListCenterCell>
      ),
    },
    !showActionsColumn
      ? null
      : consolidatedCallCentre
      ? ({
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => renderConsolidatedActions(row.original),
        } as ColumnDef<any>)
      : ({
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => {
            const rowData = row.original;
            return (
              <div className="flex items-center gap-1">
                {renderSwitchToAgentCell(rowData)}
                {isDirectParentOfAgent(rowData) && canEdit && (
                    <button
                      type="button"
                      onClick={() => handleEditAgent(rowData.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15"
                      title="Edit"
                      aria-label="Edit"
                    >
                      <FiEdit className="h-3.5 w-3.5" />
                    </button>
                  )}
                {canBlock && rowData.linkedUser?.id && !loggedInCompanyDemo && rowData.is_blocked && (
                  <button
                    type="button"
                    onClick={() => handleUnblockAgent(rowData.linkedUser.id)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200 transition-colors hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
                    title="Unblock agent (restores login)"
                    aria-label="Unblock agent"
                  >
                    <Unlock className="h-3.5 w-3.5" />
                  </button>
                )}
                {canBlock && rowData.linkedUser?.id && !loggedInCompanyDemo && !rowData.is_blocked && (
                  <button
                    type="button"
                    onClick={() => handleBlockAgent(rowData.linkedUser.id)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                    title="Block agent (revokes login)"
                    aria-label="Block agent"
                  >
                    <Ban className="h-3.5 w-3.5" />
                  </button>
                )}
                {canShowPassword && rowData.linkedUser?.id && (
                  <button
                    type="button"
                    onClick={() => handleShowAgentPassword(rowData.linkedUser.id)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15"
                    title="Show password"
                    aria-label="Show password"
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                  </button>
                )}
                {isDirectParentOfAgent(rowData) && canEdit && (
                    <button
                      type="button"
                      onClick={() => openAllocateCliModal(rowData)}
                      disabled={rowData.is_logged_in}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={
                        rowData.is_logged_in
                          ? 'Cannot allocate CLI while agent is logged in'
                          : 'Allocate CLI'
                      }
                      aria-label="Allocate CLI"
                    >
                      <PhoneForwarded className="h-3.5 w-3.5" />
                    </button>
                  )}
                <button
                  type="button"
                  onClick={() => setActivityAgent(rowData)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15"
                  title="View agent activity"
                  aria-label="View activity"
                >
                  <ActivityIcon className="h-3.5 w-3.5" />
                </button>
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={() => setSipTraceAgent(rowData)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15"
                    title="WebRTC trace (sign-in, calls reaching the agent, audio)"
                    aria-label="WebRTC trace"
                  >
                    <Radio className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setCallQualityAgent(rowData)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15"
                  title="WebRTC call quality (MOS, loss, jitter, echo)"
                  aria-label="Call quality stats"
                >
                  <Signal className="h-3.5 w-3.5" />
                </button>
                {hierarchyTargetForAgent(rowData) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openHierarchyModal(rowData);
                    }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 dark:hover:bg-white/15"
                    title="View hierarchy"
                    aria-label="View hierarchy"
                  >
                    <Network className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          },
        } as ColumnDef<any>),
  ].filter(Boolean) as ColumnDef<any>[];

  return (
    <FillPage>
      <PageHeader
        title="Agents"
        description="Provisioned agents — extensions, groups, availability, and quality snapshots."
        actions={
          <>
            {canAdd && canBulkUploadAgents() && <Button onClick={handleBulkAdd} variant="outline">Bulk Add</Button>}
            {canAdd && (
              <Button onClick={openCreateAgent} disabled={checkingCreateLicence}>
                {checkingCreateLicence ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {checkingCreateLicence ? 'Checking licence…' : 'Create Agent'}
              </Button>
            )}
          </>
        }
      />
      {isSwitchingAccount && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center backdrop-blur-sm"
          style={{ pointerEvents: 'all' }}
        >
          <div className="flex min-w-[280px] items-center gap-3.5 rounded-2xl border border-border/60 bg-card px-7 py-5 shadow-[10px_10px_24px_rgba(163,177,198,0.5),-10px_-10px_24px_rgba(255,255,255,0.9)]">
            <Loader2 className="h-7 w-7 animate-spin text-primary dark:!text-slate-100" />
            <span className="text-sm font-semibold text-foreground">
              Switching account…
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkLogoutModal(true)}
                disabled={bulkLogoutLoading || selectedAgents.length === 0}
                className="gap-1.5"
              >
                Bulk Logout ({selectedAgents.length})
                {bulkLogoutLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </Button>
            )}
            {canBulkBlock && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkStatusTarget(0)}
                  disabled={bulkStatusLoading || selectedAgents.length === 0}
                >
                  Block ({selectedAgents.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkStatusTarget(1)}
                  disabled={bulkStatusLoading || selectedAgents.length === 0}
                >
                  Unblock ({selectedAgents.length})
                </Button>
              </>
            )}
            {totalAgents !== 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={buttonDisable}
                className="gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Export
                {buttonDisable && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </Button>
            )}
            <div className="relative ml-auto">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search agents…"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="input-embossed h-10 w-[260px] rounded-md pl-8 pr-3 text-sm"
              />
            </div>
          </div>
          <div className="scroll-container agents-list-scroll">
            <DataTable
              tableId="agents"
              columns={columns}
              data={agentList}
              isLoading={pageLoading}
              pageSize={10}
              serverPagination={{ ...paging, rowCount: totalAgents, onChange: setPaging }}
              emptyTitle="No agents found."
              disableExport
              disableSearch
              getRowId={(row) => String(row.id)}
              initialColumnPinning={{ right: ['actions'] }}
            />
          </div>
        </>
      )}

      <AgentActivityModal
        show={!!activityAgent}
        onHide={() => setActivityAgent(null)}
        agentId={activityAgent?.id}
        agentName={activityAgent?.agent_name}
        agentNumber={activityAgent?.agent_number}
      />

      <SipTraceModal
        show={isSuperAdmin && !!sipTraceAgent}
        onHide={() => setSipTraceAgent(null)}
        agentId={sipTraceAgent?.id}
        agentName={sipTraceAgent?.agent_name}
        agentNumber={sipTraceAgent?.agent_number}
      />

      <RenderUserHierarchyModal
        show={showHierarchyModal}
        setShow={setShowHierarchyModal}
        user={hierarchyUser}
      />

      <AgentCallQualityModal
        show={!!callQualityAgent}
        onHide={() => setCallQualityAgent(null)}
        agentId={callQualityAgent?.id}
        agentName={callQualityAgent?.agent_name}
        agentNumber={callQualityAgent?.agent_number}
      />

      {/* View CLIs */}
      <Dialog open={showViewClisModal} onOpenChange={setShowViewClisModal}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary">
                <Phone className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Allocated CLIs</div>
                <div className="text-xs font-normal text-muted-foreground">
                  {viewClisAgent?.agent_name
                    ? `Outbound caller IDs allocated to ${viewClisAgent.agent_name}.`
                    : 'Outbound caller IDs allocated to this agent.'}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-xl border border-border/60 bg-muted/40 px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Agent
                </span>
                <div className="text-sm font-semibold text-foreground">
                  {viewClisAgent?.agent_name || '—'}
                </div>
              </div>
              <div className="ml-auto">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Total CLIs
                </span>
                <div>
                  <span className="inline-flex items-center rounded-full bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 px-2.5 py-0.5 text-xs font-semibold text-primary ring-1 ring-inset ring-primary/25 tabular-nums">
                    {viewClisList.length}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {viewClisLoading ? (
            <div className="flex items-center justify-center rounded-xl border border-border/60 bg-card py-8 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : viewClisList.length === 0 ? (
            <div className="rounded-xl border border-border/60 bg-card py-8 text-center text-sm text-muted-foreground">
              No CLIs allocated
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="w-12 px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      #
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Number
                    </th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {viewClisList.map((n, idx) => (
                    <tr
                      key={n.id}
                      className={
                        'border-t border-border/40 transition-colors hover:bg-muted/30 ' +
                        (idx % 2 === 1 ? 'bg-muted/15' : '')
                      }
                    >
                      <td className="px-4 py-2.5 text-xs tabular-nums text-muted-foreground">
                        {idx + 1}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-sm font-medium text-foreground">
                        {n.actual_number}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {n.is_default ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
                            Default
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
                            Allocated
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewClisModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agent Details */}
      <Dialog
        open={showAgentDetailsModal}
        onOpenChange={(next) => {
          if (!next) {
            setShowAgentDetailsModal(false);
            setSelectedAgentDetails(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary">
                <User className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Agent Details</div>
                <div className="text-xs font-normal text-muted-foreground">
                  {selectedAgentDetails?.agent_name || 'Read-only agent profile summary.'}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedAgentDetails &&
            (() => {
              const isBlocked = selectedAgentDetails.is_blocked;
              const rows: {
                icon: any;
                label: string;
                value: any;
                tone?: 'default' | 'success' | 'danger' | 'primary';
              }[] = [
                {
                  icon: <Phone className="h-3.5 w-3.5" />,
                  label: 'Agent Number',
                  value: selectedAgentDetails.agent_number || '—',
                },
                {
                  icon: <Mail className="h-3.5 w-3.5" />,
                  label: 'Email ID',
                  value: selectedAgentDetails.email_id || '—',
                },
                {
                  icon: <User className="h-3.5 w-3.5" />,
                  label: 'Username',
                  value:
                    selectedAgentDetails.username ||
                    selectedAgentDetails.linkedUser?.username ||
                    '—',
                },
                {
                  icon: <User className="h-3.5 w-3.5" />,
                  label: 'Created By',
                  value: selectedAgentDetails.created_by || '—',
                },
                {
                  icon: <Calendar className="h-3.5 w-3.5" />,
                  label: 'Joining Date',
                  value: selectedAgentDetails.created_at || '—',
                },
                {
                  icon: <Shield className="h-3.5 w-3.5" />,
                  label: 'Assigned ACL',
                  value: formatAgentAclDisplay(selectedAgentDetails),
                  tone: 'primary',
                },
              ];
              if (consolidatedCallCentre) {
                rows.push(
                  {
                    icon: <CalendarClock className="h-3.5 w-3.5" />,
                    label: 'Expiry Date',
                    value: formatDateTime(selectedAgentDetails.expiry_date) || '—',
                  },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Account Status',
                    value: isBlocked ? 'Blocked' : 'Active',
                    tone: isBlocked ? 'danger' : 'success',
                  },
                  {
                    icon: <User className="h-3.5 w-3.5" />,
                    label: 'Access',
                    value:
                      selectedAgentDetails.access === 'team_lead'
                        ? 'Team Lead'
                        : selectedAgentDetails.access === 'regular'
                        ? 'Regular'
                        : selectedAgentDetails.access || '—',
                  },
                  {
                    icon: <User className="h-3.5 w-3.5" />,
                    label: 'First Call Acceptance',
                    value: selectedAgentDetails.first_call_acceptance || '—',
                  },
                  // cli_option removed: always actual
                  // {
                  //   icon: <Phone className="h-3.5 w-3.5" />,
                  //   label: 'CLI Option (Webrtc)',
                  //   value: selectedAgentDetails.cli_option || '—',
                  // },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Outbound Enabled',
                    value: selectedAgentDetails.is_outbound_enabled ? 'Yes' : 'No',
                    tone: selectedAgentDetails.is_outbound_enabled ? 'success' : 'default',
                  },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Number Masking',
                    value: selectedAgentDetails.is_masking_enabled ? 'Yes' : 'No',
                    tone: selectedAgentDetails.is_masking_enabled ? 'success' : 'default',
                  },
                );
              }
              rows.push(
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Auto Answer',
                    value: selectedAgentDetails.auto_pick_enabled ? 'Yes' : 'No',
                    tone: selectedAgentDetails.auto_pick_enabled ? 'success' : 'default',
                  },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Auto Answer Shown to Agent',
                    value: selectedAgentDetails.show_auto_answer_toggle ? 'Yes' : 'No',
                    tone: selectedAgentDetails.show_auto_answer_toggle ? 'success' : 'default',
                  },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Transfer',
                    value: selectedAgentDetails.is_transfer_enabled ? 'Yes' : 'No',
                    tone: selectedAgentDetails.is_transfer_enabled ? 'success' : 'default',
                  },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Conference',
                    value: selectedAgentDetails.is_conference_enabled ? 'Yes' : 'No',
                    tone: selectedAgentDetails.is_conference_enabled ? 'success' : 'default',
                  },
                  {
                    icon: <Shield className="h-3.5 w-3.5" />,
                    label: 'Early Follow-up Call',
                    value: selectedAgentDetails.allow_early_follow_up ? 'Yes' : 'No',
                    tone: selectedAgentDetails.allow_early_follow_up ? 'success' : 'default',
                  },
              );
              if (consolidatedCallCentre) {
                rows.push(
                  {
                    icon: <User className="h-3.5 w-3.5" />,
                    label: 'Allocation Status',
                    value:
                      Number(selectedAgentDetails.is_allocated) === 0 ? 'Available' : 'Allocated',
                    tone: Number(selectedAgentDetails.is_allocated) === 0 ? 'success' : 'primary',
                  },
                );
              }
              const toneClass = (t?: string) => {
                if (t === 'success')
                  return 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300';
                if (t === 'danger')
                  return 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300';
                if (t === 'primary')
                  return 'bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-primary/25';
                return '';
              };
              return (
                <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
                  <div className="grid grid-cols-1 divide-y divide-border/40 md:grid-cols-2 md:divide-y-0">
                    {rows.map((r, idx) => {
                      const rightBorder =
                        idx % 2 === 0 ? 'md:border-r md:border-border/40' : '';
                      const topBorder = idx >= 2 ? 'md:border-t md:border-border/40' : '';
                      return (
                        <div
                          key={r.label}
                          className={
                            'flex items-center gap-3 px-4 py-3 ' +
                            rightBorder +
                            ' ' +
                            topBorder
                          }
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary ring-1 ring-inset ring-primary/20">
                            {r.icon}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              {r.label}
                            </div>
                            <div className="mt-0.5">
                              {r.tone ? (
                                <span
                                  className={
                                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ' +
                                    toneClass(r.tone)
                                  }
                                >
                                  {r.value}
                                </span>
                              ) : (
                                <span className="break-words text-sm font-semibold text-foreground">
                                  {r.value}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAgentDetailsModal(false);
                setSelectedAgentDetails(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate CLI */}
      <Dialog
        open={showAllocateCliModal}
        onOpenChange={(next) => {
          if (!next && !savingCliDid) setShowAllocateCliModal(false);
        }}
      >
        <DialogContent
          className="max-h-[85vh] max-w-lg overflow-visible allocate-cli-modal"
          onPointerDownOutside={(e) => {
            if (
              isAllocateCliSelectNode(e.target) ||
              isAllocateCliSelectNode(e.detail?.originalEvent?.target)
            ) {
              e.preventDefault();
            }
          }}
          onFocusOutside={(e) => {
            if (
              isAllocateCliSelectNode(e.target) ||
              isAllocateCliSelectNode(e.detail?.originalEvent?.target)
            ) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (
              isAllocateCliSelectNode(e.target) ||
              isAllocateCliSelectNode(e.detail?.originalEvent?.target)
            ) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary">
                <PhoneForwarded className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Allocate CLI</div>
                <div className="text-xs font-normal text-muted-foreground">
                  Choose which caller IDs this agent may use for outbound calls.
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-xl border border-border/60 bg-muted/40 px-4 py-3">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Agent
            </span>
            <div className="text-sm font-semibold text-foreground">
              {allocatingAgent?.agent_name || '—'}
            </div>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Select CLI</label>
              <div className="relative z-20">
                <ReactSelect
                  isMulti
                  classNamePrefix="allocate-cli-select"
                  value={availableCliDids
                    .filter((n) => selectedCliDidIds.includes(Number(n.id)))
                    .map((n) => ({ value: Number(n.id), label: `${String(n.actual_number)} (CLI)` }))}
                  options={availableCliDids.map((n) => ({
                    value: Number(n.id),
                    label: `${String(n.actual_number)} (CLI)`,
                  }))}
                  onChange={(sel: any) => handleCliDidSelect((sel || []).map((o) => o.value))}
                  isOptionDisabled={() => selectedCliDidIds.length >= MAX_AGENT_CLIS}
                  isDisabled={loadingCliDids || savingCliDid}
                  placeholder={`Select CLI(s) (max ${MAX_AGENT_CLIS})`}
                  className="w-100"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 50, pointerEvents: 'auto' }),
                    menuList: (base) => ({ ...base, maxHeight: 240 }),
                  }}
                />
              </div>
              <div
                className={`text-xs ${selectedCliDidIds.length > MAX_AGENT_CLIS ? 'text-destructive' : 'text-muted-foreground'}`}
              >
                {selectedCliDidIds.length} of {MAX_AGENT_CLIS} selected
                {selectedCliDidIds.length > MAX_AGENT_CLIS ? ' — remove some to save' : ''}
              </div>
              <div className="text-xs text-muted-foreground">
                Default CLI:{' '}
                <strong>
                  {(() => {
                    const defaultId = selectedCliDidIds?.[0];
                    const defaultDid = availableCliDids.find((n) => Number(n.id) === Number(defaultId));
                    return defaultDid ? `${defaultDid.actual_number}` : 'Not selected';
                  })()}
                </strong>
              </div>
            </div>

            {!!selectedCliDidIds?.length && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Dedicated CLI (one number)
                </label>
                <div className="max-h-[200px] overflow-y-auto rounded-lg border border-border/60 bg-card p-2">
                  <label className="mb-1 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-muted/40">
                    <input
                      type="radio"
                      id="dedicated-cli-none"
                      name="agentDedicatedCli"
                      checked={dedicatedCliNumberId === null}
                      onChange={() => setDedicatedCliNumberId(null)}
                      disabled={savingCliDid}
                      className="accent-primary"
                    />
                    <span className="text-muted-foreground">None</span>
                  </label>
                  {selectedCliDidIds.map((didId) => {
                    const did = availableCliDids.find(
                      (n) => Number(n.id) === Number(didId),
                    );
                    if (!did) return null;
                    return (
                      <label
                        key={`dedicated-cli-${didId}`}
                        className="mb-0.5 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-muted/40"
                      >
                        <input
                          type="radio"
                          id={`dedicated-cli-${didId}`}
                          name="agentDedicatedCli"
                          checked={dedicatedCliNumberId === Number(didId)}
                          onChange={() => setDedicatedCliNumberId(Number(didId))}
                          disabled={savingCliDid}
                          className="accent-primary"
                        />
                        <span className="font-mono">{did.actual_number}</span>
                        <span className="text-xs text-muted-foreground">(CLI)</span>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-primary/25 bg-primary/[0.06] p-3 text-xs text-foreground">
                  <FiInfo size={18} className="mt-0 flex-shrink-0 text-primary dark:!text-slate-100" aria-hidden />
                  <span>
                    For a dedicated CLI, open <strong>Numbers</strong> → inbound routing for
                    that number and set the <strong>time schedule</strong> as needed.
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAllocateCliModal(false)}
              disabled={savingCliDid}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveAllocatedCliDid} disabled={savingCliDid} className="gap-2">
              {savingCliDid && <Loader2 className="h-4 w-4 animate-spin" />}
              {savingCliDid ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add */}
      <Dialog open={showBulkAddModal} onOpenChange={setShowBulkAddModal}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary">
                <User className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Bulk Add Agents</div>
                <div className="text-xs font-normal text-muted-foreground">
                  Upload a CSV file to onboard multiple agents at once.
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <label htmlFor="formFile" className="text-sm font-medium text-foreground">
                Upload .csv File
              </label>
              <Input
                id="formFile"
                type="file"
                accept=".csv"
                onChange={handleBulkAddFileChange}
              />
            </div>
            <InfoNote>
              The details uploaded for each agent are also used to automatically create a corresponding user.
              Agent Name becomes the user&apos;s display name. Username and Password are used exactly as
              provided for login.
              <br />
              <br />
              <strong>Required Fields:</strong> Agent Name, Agent Number, Email ID, Username, Password,
              Access, Type (must be allowed by your Agents ACL: Mobile / Soft Phone / WEBRTC),
              Extension ID (if Type includes Soft Phone or WEBRTC)
              <br />
              <strong>Optional Fields:</strong> Description, Is Outbound Enabled, Masking, First Call
              Connect, ACL Settings ID, Account Type, Expiry Date, Domain, Panel
              Display Name, Logo Path
              <br />
              <strong>Country codes:</strong> Mobile agents use country code 91. Every agent gets all country codes
              assigned to your call center; if your call center has none, agents are created without country codes.
              <br />
              <em>Username is separate from Agent Name. Download the template for the exact format.</em>
            </InfoNote>
            <Button
              size="sm"
              onClick={(event) => {
                const { columns: cols, requirements, filename, sample_data } = getColumnsAndFilename();
                downloadTemplate(event, cols, filename, requirements, sample_data);
              }}
            >
              Download Template
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAddModal(false)}>
              Close
            </Button>
            <Button onClick={handleBulkAddSave} disabled={bulkAddSaving}>
              {bulkAddSaving ? 'Uploading…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Result */}
      <Dialog
        open={showBulkResultModal}
        onOpenChange={(next) => {
          if (!next) {
            setShowBulkResultModal(false);
            setBulkUploadResult(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-white/10 dark:!text-slate-100 dark:ring-white/20 text-primary">
                <Info className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">
                  Bulk Upload Result
                </div>
                <div className="text-xs font-normal text-muted-foreground">
                  Summary of the agents processed from the uploaded CSV.
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          {bulkUploadResult && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Processed
                  </div>
                  <div className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                    {bulkUploadResult.totalProcessed}
                  </div>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-900/40 dark:bg-emerald-950/30">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                    Created
                  </div>
                  <div className="mt-1 text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                    {bulkUploadResult.successCount}
                  </div>
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 dark:border-rose-900/40 dark:bg-rose-950/30">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-rose-700 dark:text-rose-400">
                    Failed
                  </div>
                  <div className="mt-1 text-2xl font-bold tabular-nums text-rose-700 dark:text-rose-300">
                    {bulkUploadResult.failedCount}
                  </div>
                </div>
              </div>

              {bulkUploadResult.successRows?.length > 0 && (
                <div className="space-y-2">
                  <h5 className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Agents Created ({bulkUploadResult.successRows.length})
                  </h5>
                  <textarea
                    readOnly
                    rows={4}
                    className="w-full rounded-md border border-border/60 bg-card p-3 font-mono text-xs text-foreground shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)] outline-none focus:border-ring"
                    value={bulkUploadResult.successRows
                      .map((item, index) => formatSuccessAgent(item, index))
                      .join('\n')}
                  />
                </div>
              )}

              {bulkUploadResult.failedRows?.length > 0 && (
                <div className="space-y-2">
                  <h5 className="flex items-center gap-2 text-sm font-semibold text-rose-700 dark:text-rose-400">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-rose-500" />
                    Failed Rows ({bulkUploadResult.failedRows.length})
                  </h5>
                  <textarea
                    readOnly
                    rows={6}
                    className="w-full rounded-md border border-border/60 bg-card p-3 font-mono text-xs text-foreground shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)] outline-none focus:border-ring"
                    value={bulkUploadResult.failedRows
                      .map((item, index) => formatFailedAgent(item, index))
                      .join('\n')}
                  />
                </div>
              )}

              {bulkUploadResult.successCount === 0 &&
                bulkUploadResult.failedCount === 0 && (
                  <div className="rounded-xl border border-border/60 bg-muted/40 p-6 text-center text-sm text-muted-foreground">
                    No rows were processed from the uploaded file.
                  </div>
                )}
            </>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowBulkResultModal(false);
                setBulkUploadResult(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Logout confirm */}
      <ConfirmDialog
        open={showBulkLogoutModal}
        onOpenChange={(open) => {
          if (!open && bulkLogoutLoading) return;
          setShowBulkLogoutModal(open);
        }}
        title="Bulk Logout Agents"
        description="Are you sure you want to logged out the Agent(s)."
        confirmLabel="Save"
        cancelLabel="Close"
        loading={bulkLogoutLoading}
        onConfirm={handleAgentLogout}
      />

      <ConfirmDialog
        open={bulkStatusTarget != null}
        onOpenChange={(open) => {
          if (!open && !bulkStatusLoading) setBulkStatusTarget(null);
        }}
        title={bulkStatusTarget === 0 ? 'Block selected agents' : 'Unblock selected agents'}
        description={
          bulkStatusTarget === 0
            ? `Block ${selectedAgents.length} agent(s)? They lose login access. Agents already blocked are skipped.`
            : `Unblock ${selectedAgents.length} agent(s)? Their login access is restored. Agents already active are skipped.`
        }
        confirmLabel={bulkStatusTarget === 0 ? 'Block' : 'Unblock'}
        cancelLabel="Close"
        loading={bulkStatusLoading}
        onConfirm={handleBulkStatus}
      />

      <ConfirmDialog
        open={pendingTypeLogin !== null}
        onOpenChange={(o) => {
          if (!o && !isLoggingIn) setPendingTypeLogin(null);
        }}
        title={
          pendingTypeLogin?.isActive
            ? `Re-login as ${pendingTypeLogin?.type}?`
            : `Log in as ${pendingTypeLogin?.type}?`
        }
        description={
          <>
            Are you sure you want to log in{' '}
            <span className="font-medium text-foreground">
              {pendingTypeLogin?.agentName}
            </span>{' '}
            as{' '}
            <span className="font-medium text-foreground">
              {pendingTypeLogin?.type}
            </span>
            ?
          </>
        }
        confirmLabel="Log in"
        loading={isLoggingIn}
        onConfirm={confirmTypeLogin}
      />

      <CountryCodeDirectionsModal
        show={showCcDirectionsModal}
        onHide={() => {
          setShowCcDirectionsModal(false);
          setCcDirectionsData([]);
          setCcDirectionsOwner('');
        }}
        title="Agent Country Codes"
        ownerLabel="Agent Name"
        ownerName={ccDirectionsOwner}
        directions={ccDirectionsData}
      />

      <RenderInactiveUserModal
        show={enableInactiveModal}
        setShow={setEnableInactiveModal}
        setConfirmUserInactive={setConfirmUserInactive}
        clearSelectedData={() => {}}
      />

      <RenderShowPasswordModal
        show={enableShowPasswordModal}
        setShow={setEnableShowPasswordModal}
        setConfirmShowPassword={setConfirmShowPassword}
        passwordValue={showPasswordUser}
        userId={showPasswordId}
        onPasswordReset={() => {
          if (showPasswordId && activeUserAccessRole) {
            getApiCall(`${API_USERS_LIST}/${showPasswordId}`, { role: activeUserAccessRole })
              .then((resp: any) => {
                if (resp?.actual_password) setShowPasswordUser(`Password is : ${resp.actual_password}`);
              })
              .catch(() => {});
          }
        }}
      />
    </FillPage>
  );
};

export default Agents;
